import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import {
  IChangeMsisdnOrchestrator,
  IChangeSimEsimOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { pushSimpleEvent } from "@nju/data-layer";
import { portalRoutes } from "@nju/routes";
import { BlockContent, CircleLoader, GeneralErrorNotification } from "@nju/ui";
import { ChangeSimEsimBanner } from "./ChangeSimEsimBanner";
import { getChangeSimEsimButtonsCondition } from "./ChangeSimEsimBanner/getChangeSimEsimButtonsConditions";
import { MsisdnSettings } from "./MsisdnSettings";
import { mySimCardPageMachine } from "./mySimCardPage.machine";
import { SimEsimSettings } from "./SimEsimSettings";

export function MySimCardPage() {
  const router = useRouter();
  const params = portalRoutes.mojeKonto.mojaKarta.index
    .input(router.query)
    .unwrap();
  const { mobileProducts } = useMemberProducts();
  const { changeSimEsim, changeMsisdn, mobile } = useMemberOrchestrator();
  const clientNumber = mobileProducts.mobileAccess?.characteristic?.msisdn;
  const simNumber = mobileProducts.simCard?.characteristic?.sim;
  const simId = mobileProducts.simCard?.id;
  const cardStatus =
    mobileProducts.mobileAccess?.characteristic?.barringAdmFull;
  const orderSimNumber = changeSimEsim?.orderSimNumber;
  const isEsimProducts =
    mobileProducts?.simCard?.characteristic?.simType === "ESIM";
  const isUnsubscribedOrDisconnected = !!(
    mobileProducts.mobileAccess?.status === "Unsubscribed" ||
    mobileProducts.mobileAccess?.status === "Disconnected"
  );

  const [current, send] = useMachine(mySimCardPageMachine, {
    context: {
      iccId: orderSimNumber || simNumber,
      simNumber,
      userId: params?.userId,
      changeSimEsim,
      isEsimProducts,
    },
  });
  const { context, matches } = current;
  const { pukNumber, subState } = context;
  const puk = pukNumber?.value;

  const isActiveButton = React.useMemo(() => {
    return getChangeSimEsimButtonsCondition({
      subState,
      changeMsisdnOrchestrator: changeMsisdn as IChangeMsisdnOrchestrator,
      changeSimEsimOrchestrator: changeSimEsim as IChangeSimEsimOrchestrator,
    });
  }, [subState, changeMsisdn, changeSimEsim]);

  const handlePushDataLayer = React.useCallback((eventName: string) => {
    pushSimpleEvent({ event: "activity_service", eventName });
  }, []);

  const handleDeleteChangeSimEsim = React.useCallback(() => {
    send({
      type: "DELETE_CHANGE_SIM_ESIM",
    });
  }, [send]);

  if (matches("loading")) {
    return (
      <BlockContent border="none" rounded="primary" spacing="l" tone="none">
        <div className="flex justify-center items-center h-full">
          <CircleLoader />
        </div>
      </BlockContent>
    );
  }

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return window.location.reload();
        }}
      />
    );
  }

  return (
    <>
      <ChangeSimEsimBanner
        changeSimEsimOrchestrator={changeSimEsim}
        deleteOrchestrator={handleDeleteChangeSimEsim}
        mobileOrchestrator={mobile}
        simCardProduct={mobileProducts.simCard}
        simId={simId}
        simNumber={simNumber}
        subState={subState}
        userId={params?.userId}
      />
      <div className="flex gap-2 items-start only-mobile:flex-col">
        <MsisdnSettings
          clientNumber={clientNumber}
          handlePushDataLayer={handlePushDataLayer}
          isActiveButton={isActiveButton}
          isUnsubscribedOrDisconnected={isUnsubscribedOrDisconnected}
          userId={params?.userId}
        />
        <SimEsimSettings
          cardStatus={cardStatus}
          isActiveButton={isActiveButton}
          isEsim={isEsimProducts}
          isUnsubscribedOrDisconnected={isUnsubscribedOrDisconnected}
          pukNumber={puk}
          simNumber={simNumber}
          userId={params?.userId}
        />
      </div>
    </>
  );
}
